import { ReactNode, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { MobileNav } from "./MobileNav";
import { TopNav } from "./TopNav";
import { CelebrationToast } from "./CelebrationToast";
import { SubscriptionPaywall } from "./SubscriptionPaywall";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AppLayoutProps {
  children: ReactNode;
}

const MAX_LOADING_DURATION = 6000; // Reduced to 6 seconds for faster recovery

export function AppLayout({ children }: AppLayoutProps) {
  const { user, isLoading: authLoading, subscription } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fallback timeout for loading state
  useEffect(() => {
    if (authLoading && !loadingTimeout) {
      timeoutRef.current = setTimeout(() => {
        console.warn("AppLayout: Loading exceeded maximum duration, forcing recovery");
        setLoadingTimeout(true);
      }, MAX_LOADING_DURATION);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [authLoading, loadingTimeout]);

  // Auth loading state
  if (authLoading && !loadingTimeout) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <h2 className="text-lg font-medium font-sans text-foreground mb-2">Syncing colony data...</h2>
      </div>
    );
  }

  // If loading timed out and no user, show recovery UI
  if (loadingTimeout && !user) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 text-center">
        <h2 className="text-lg font-medium font-sans text-foreground mb-2">Session expired</h2>
        <p className="text-muted-foreground mb-4">Please sign in again to continue.</p>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => window.location.reload()}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Reload
          </Button>
          <Button onClick={() => navigate("/auth")}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  // If user is not logged in, ProtectedRoute (parent) will handle it
  if (!user && !authLoading) {
     return null; 
  }

  // Subscription check - show paywall if not subscribed
  if (subscription !== null && subscription !== undefined && !subscription.subscribed) {
    return <SubscriptionPaywall />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top navigation for desktop/tablet */}
      {!isMobile && <TopNav />}

      {/* Main content with appropriate padding */}
      <main className={cn(isMobile ? "bottom-nav-padding" : "pt-16")}>{children}</main>

      {/* Bottom navigation for mobile only */}
      {isMobile && <MobileNav />}

      {/* Celebration overlay */}
      <CelebrationToast />
    </div>
  );
}
